import type { LibraryAsset } from './types';

// The asset library is whatever the CDN says it is: each pack publishes a
// manifest.json beside its GLBs (written by the template's asset pipeline),
// and the palette is built from those rather than a hardcoded list, so
// uploading a new asset to R2 is all it takes to see it in the editor.

export const PACKS_URL = 'https://assets.augmento.com/packs';

// Packs are DISCOVERED, not hardcoded.
//
// The bucket publishes packs/index.json (written by the template's
// scripts/publish-packs.mjs on every upload), so an asset pack created for one
// project shows up in every other project's palette without a code change in
// any of them. That index is the whole reason the library is shared rather
// than merely hosted: R2 has no listing over plain HTTP, so without it each
// viewer would have to be told which packs exist.
//
// The fallback list is what shipped before the index existed. It keeps the
// palette working against an older bucket, or if the index is ever mid-write.
const FALLBACK_PACKS = ['plants', 'furniture', 'cars', 'exterior'];

// People are excluded on purpose: they are skinned meshes, and the runtime
// draws layers as InstancedMesh, which has no skinning path. Crowd systems
// (see pedestrians.ts in Diriyah) own animated people.
const EXCLUDED_PACKS = new Set(['people']);

interface PackIndex {
    updated: string;
    packs: { name: string; assets: number; placeable: number }[];
}

async function discoverPacks(): Promise<string[]> {
    try {
        const res = await fetch(`${PACKS_URL}/index.json`, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const index = (await res.json()) as PackIndex;
        const names = index.packs.map((p) => p.name).filter((n) => !EXCLUDED_PACKS.has(n));
        if (names.length) return names;
        throw new Error('index listed no usable packs');
    } catch (error) {
        console.warn('[placement] pack index unavailable, using fallback list:', error);
        return FALLBACK_PACKS;
    }
}

// Assets the module builds itself rather than fetching. They live in the
// palette beside the CDN packs; assetUrl() never sees them because the
// instancing layer branches on the "generated/" prefix.
export const GENERATED_ASSETS: LibraryAsset[] = [
    {
        id: 'generated/lantern',
        pack: 'lights',
        name: 'lantern',
        file: '',
        tris: 220,
        kb: 0,
        tier: 'deco',
    },
];

export function isGenerated(assetId: string): boolean {
    return assetId.startsWith('generated/');
}

interface ManifestEntry {
    name: string;
    file: string;
    tris: number;
    kb: number;
    tier: string;
}

export async function fetchLibrary(): Promise<LibraryAsset[]> {
    const packs = await discoverPacks();
    const results = await Promise.allSettled(
        packs.map(async (pack) => {
            const res = await fetch(`${PACKS_URL}/${pack}/manifest.json`);
            if (!res.ok) throw new Error(`${pack}: HTTP ${res.status}`);
            const manifest = (await res.json()) as { assets: ManifestEntry[] };
            // The _lite variants are decimated LOD bakes for budget tiers, not
            // hero placements — at authoring distance they read as fake. They
            // stay on the CDN (the template's budget system uses them); the
            // palette just never offers them.
            return manifest.assets
                .filter((a) => !a.name.endsWith('_lite'))
                .map(
                (a): LibraryAsset => ({
                    id: `${pack}/${a.name}`,
                    pack,
                    name: a.name,
                    file: a.file,
                    tris: a.tris,
                    kb: a.kb,
                    tier: a.tier,
                }),
            );
        }),
    );
    // A pack failing to load hides that pack, not the library: the CDN being
    // half-reachable should degrade the palette, not blank it.
    const assets = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
    for (const r of results) {
        if (r.status === 'rejected') console.warn('[placement] pack skipped:', r.reason);
    }
    return [...assets, ...GENERATED_ASSETS];
}

export function assetUrl(asset: LibraryAsset): string {
    return `${PACKS_URL}/${asset.pack}/${asset.file}`;
}
